/**
 * AI-brand gradient — the purple → indigo → blue → cyan sweep used across the
 * AI subject-line UI (modal header, refine panel, the "Generate with AI"
 * button) and the {@link AiWandIcon}. Centralized here so the brand ramp lives
 * in one place instead of being re-hardcoded at each call site.
 */

// Canonical brand stops. `rgb` is the comma-separated channel triplet used to
// build tinted (low-opacity) sweeps; `hex` is the solid value used for borders
// and the SVG icon fill.
export const AI_BRAND_STOPS = [
  { offset: 0, rgb: '144,26,255', hex: '#901AFF' },
  { offset: 0.33, rgb: '98,0,234', hex: '#6200EA' },
  { offset: 0.66, rgb: '63,96,188', hex: '#3F60BC' },
  { offset: 1, rgb: '20,189,208', hex: '#14BDD0' },
] as const;

// Brand purple — used for the AI button / refine input border (no palette token
// exists for #901AFF).
export const AI_BRAND_PURPLE = AI_BRAND_STOPS[0].hex;

const pct = (offset: number) => `${Math.round(offset * 100)}%`;

/**
 * Solid hex gradient — for the icon fill and the refine input's gradient
 * border. Defaults to the 135° diagonal used by both.
 */
export const aiBrandGradient = (angleDeg = 135): string =>
  `linear-gradient(${angleDeg}deg, ${AI_BRAND_STOPS.map(
    (s) => `${s.hex} ${pct(s.offset)}`,
  ).join(', ')})`;

/**
 * Tinted sweep at a given opacity — for the soft brand backgrounds on the modal
 * header, the expanded refine panel, and the AI button hover state.
 */
export const aiBrandSweep = (alpha: number, angleDeg = 90): string =>
  `linear-gradient(${angleDeg}deg, ${AI_BRAND_STOPS.map(
    (s) => `rgba(${s.rgb},${alpha}) ${pct(s.offset)}`,
  ).join(', ')})`;

/**
 * Tinted sweep composited over a solid white base — the soft AI-brand card
 * background used behind info banners (e.g. {@link AiInfoBanner}) and the
 * "Generating Guidelines" card. Defaults to the 90° horizontal sweep both
 * call sites use.
 */
export const aiSoftSurface = (alpha: number, angleDeg = 90): string =>
  `${aiBrandSweep(alpha, angleDeg)}, linear-gradient(${angleDeg}deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)`;

/**
 * AI "glow" — four soft, brand-colored drop shadows layered around the refine
 * input to give it the signature AI halo.
 */
export const AI_GLOW_SHADOW = [
  '3px 4px 8px 0px rgba(20,189,208,0.08)',
  '-3px 4px 8px 0px rgba(63,96,188,0.08)',
  '2px -2px 8px 0px rgba(98,0,234,0.08)',
  '-2px -2px 8px 0px rgba(144,26,255,0.08)',
].join(', ');
